import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, INQUIRY_STATUS_LABEL, INQUIRY_SOURCE_LABEL, fmtDateTime, getUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Inbox as InboxIcon } from "lucide-react";

const FILTERS = [
  { key: "", label: "Alle" },
  { key: "neu", label: "Neu" },
  { key: "in_pruefung", label: "In Prüfung" },
  { key: "in_auftrag_umgewandelt", label: "Umgewandelt" },
  { key: "abgelehnt", label: "Abgelehnt" },
];

const STATUS_CLASS = {
  neu: "bg-slate-100 text-slate-700 border-slate-300",
  in_pruefung: "bg-blue-50 text-blue-700 border-blue-200",
  in_auftrag_umgewandelt: "bg-emerald-50 text-emerald-700 border-emerald-200",
  abgelehnt: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function Anfragen() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [nurMeine, setNurMeine] = useState(() => localStorage.getItem("anfragen_nur_meine") === "1");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", telefon: "", firma: "", nachricht: "", quelle: "manuell" });
  const user = getUser();

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    const r = await api.get("/anfragen", { params });
    let data = r.data;
    if (nurMeine && user) data = data.filter((a) => a.assigned_user_id === user.id);
    setList(data);
  };

  useEffect(() => { load();  }, [q, status, nurMeine]);
  useEffect(() => { localStorage.setItem("anfragen_nur_meine", nurMeine ? "1" : "0"); }, [nurMeine]);

  const create = async () => {
    if (!form.nachricht) { toast.error("Nachricht ist erforderlich."); return; }
    try {
      const r = await api.post("/anfragen", form);
      toast.success(`Anfrage ${r.data.anfrage_nummer} angelegt.`);
      setDialogOpen(false);
      setForm({ name: "", email: "", telefon: "", firma: "", nachricht: "", quelle: "manuell" });
      nav(`/anfragen/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen."); }
  };

  return (
    <div className="space-y-6" data-testid="anfragen-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Anfragen</h1>
          <p className="text-sm text-slate-500 mt-1">Kundenanfragen (WordPress, Telefon, manuell) verwalten und in Aufträge umwandeln.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="anfrage-add-button"><Plus className="w-4 h-4 mr-2" /> Anfrage anlegen</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f.key || "alle"} onClick={() => setStatus(f.key)} data-testid={`anfrage-filter-${f.key || "alle"}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${status === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
            {f.label}
          </button>
        ))}
        <button onClick={() => setNurMeine(!nurMeine)} data-testid="anfrage-filter-nur-meine"
          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${nurMeine ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
          Nur meine
        </button>
        <div className="relative flex-1 min-w-[240px] ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche…" className="pl-9" data-testid="anfrage-search-input" />
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {list.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              <InboxIcon className="w-10 h-10 mx-auto mb-3 text-slate-400" />
              Keine Anfragen gefunden.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="anfrage-table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Nummer</th>
                  <th className="px-4 py-3">Absender</th>
                  <th className="px-4 py-3">Nachricht</th>
                  <th className="px-4 py-3">Quelle</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Eingang</th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a.id} onClick={() => nav(`/anfragen/${a.id}`)}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
                    data-testid={`anfrage-row-${a.anfrage_nummer}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{a.anfrage_nummer}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{a.name || a.email || "(unbekannt)"}</div>
                      <div className="text-xs text-slate-500">{a.email}{a.firma ? ` · ${a.firma}` : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-md truncate">{a.nachricht}</td>
                    <td className="px-4 py-3"><Badge variant="outline">{INQUIRY_SOURCE_LABEL[a.quelle] || a.quelle}</Badge></td>
                    <td className="px-4 py-3">
                      <span className={`badge-status ${STATUS_CLASS[a.status]}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        {INQUIRY_STATUS_LABEL[a.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{fmtDateTime(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="anfrage-create-dialog">
          <DialogHeader><DialogTitle>Anfrage manuell anlegen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="anfrage-name-input" /></div>
              <div><Label>Firma</Label><Input value={form.firma} onChange={(e) => setForm({ ...form, firma: e.target.value })} data-testid="anfrage-firma-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>E-Mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="anfrage-email-input" /></div>
              <div><Label>Telefon</Label><Input value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} data-testid="anfrage-telefon-input" /></div>
            </div>
            <div>
              <Label>Quelle</Label>
              <Select value={form.quelle} onValueChange={(v) => setForm({ ...form, quelle: v })}>
                <SelectTrigger data-testid="anfrage-quelle-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manuell">Manuell</SelectItem>
                  <SelectItem value="telefon">Telefon</SelectItem>
                  <SelectItem value="vor_ort">Vor Ort</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Nachricht</Label><Textarea rows={4} value={form.nachricht} onChange={(e) => setForm({ ...form, nachricht: e.target.value })} data-testid="anfrage-nachricht-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={create} data-testid="anfrage-save-button">Anlegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
